// MonthPicker — jump to a date; days that have entries are marked (REQUIREMENTS_NOTEBOOK NB-7, BOLUS_NOTES-83).
import React, { useState } from 'react';

const pad = (n: number) => String(n).padStart(2, '0');

/** Weeks (Sunday first) of `YYYY-MM` as YYYY-MM-DD strings, null outside the month. */
export function monthGrid(ym: string): (string | null)[][] {
  const [y, m] = ym.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells: (string | null)[] = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(`${y}-${pad(m)}-${pad(d)}`);
  while (cells.length % 7) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** `YYYY-MM` plus `n` months. */
export function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${pad((t % 12) + 1)}`;
}

interface Props {
  value: string;             // the date shown (YYYY-MM-DD)
  today: string;             // in the profile timezone
  marked: Set<string>;       // days with entries
  onPick: (date: string) => void;
}

const MonthPicker: React.FC<Props> = ({ value, today, marked, onPick }) => {
  const [open, setOpen] = useState(false);
  const [ym, setYm] = useState(value.slice(0, 7));
  const label = new Date(value + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <div className="nb-month">
      <button className="nb-date" onClick={() => { setYm(value.slice(0, 7)); setOpen(!open); }} aria-expanded={open} aria-label="Pick a date">{label} ▾</button>
      {open && (
        <div className="nb-month-pop" role="dialog" aria-label="Pick a date">
          <div className="nb-month-head">
            <button onClick={() => setYm(addMonths(ym, -1))} aria-label="Previous month">‹</button>
            <span>{new Date(ym + '-15T12:00:00').toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
            <button onClick={() => setYm(addMonths(ym, 1))} disabled={ym >= today.slice(0, 7)} aria-label="Next month">›</button>
          </div>
          <div className="nb-month-grid">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i} className="nb-month-dow">{d}</span>)}
            {monthGrid(ym).flat().map((d, i) => d === null ? <span key={i} /> : (
              <button key={d} disabled={d > today} onClick={() => { setOpen(false); onPick(d); }}
                className={`${marked.has(d) ? 'has' : ''} ${d === value ? 'sel' : ''} ${d === today ? 'today' : ''}`}
                aria-label={`${d}${marked.has(d) ? ', has entries' : ''}`}>{Number(d.slice(8))}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
